import { WIX_AI_CHAT_ASSISTANT, WIX_CHAT } from '@wix/app-definition-ids';
import {
  DocumentServicesObject,
  EditorClientSpecMapEntry,
} from '@wix/document-services-types';
import type {
  IntentInstallOptions,
  IntentInstallReturnValue,
  PlatformApiMethods,
} from '@wix/editor-platform-host-integration-apis';
import { uniq } from 'lodash';
import { PlatformContext } from '../../../types/platformApi';
import { UnifiedComponentsCollection } from '../../../types/unifiedComponents';
import { getEditorInfo, getIsImpersonated } from '../../../utils';
import { appMarketService } from '../../utils/appMarketService';
import { getAppType } from '../../utils/appMarketUtils';
import { IExperimentsApiAdapter } from '../../utils/experimentsApiAdapter';
import { getPendingApps } from '../../utils/pendingAppsService';
import { ApplicationsInstaller } from './ApplicationsInstaller/ApplicationsInstaller';
import { SerialInstallationStrategyV2 } from './ApplicationsInstaller/SerialInstallationStrategyV2';
import { BIManager, IntentInstallBIEventStep } from './BIManager';
import { IntentApp, IntentInstallStore } from './IntentInstallStore';
import { PanelsManager } from './PanelsManager';
import {
  type ProgressBarManager,
  createProgressBarManager,
} from './ProgressBarManager';
import {
  PRE_INTENT_INSTALL_MODAL_EXPERIMENT,
  WIDGET_APPS_CONFIG_EXPERIMENT,
} from './constants';
import { IntentInstallErrorCode, createIntentInstallError } from './errors';
import {
  getIntentAppInfoFromQueryParams,
  getIntentInstallTestParams,
} from './helpers';

type GetBiExtrasOptions = {
  appDefinitionId?: string;
};

// currently in intent install we automatically install only these widget apps
const allowedWidgetApps = new Set([WIX_CHAT, WIX_AI_CHAT_ASSISTANT]);

const canInstallApp =
  (options: IntentInstallOptions) => (appData: EditorClientSpecMapEntry) => {
    if (options.shouldInstallWidgets) {
      return true;
    }

    const type = getAppType(appData);
    return (
      allowedWidgetApps.has(appData.appDefinitionId) || type !== 'tpaWidget'
    );
  };

const getIntentAppFromQueryParams = (
  ignoredApps: string[],
): IntentApp | undefined => {
  const intentApp = getIntentAppInfoFromQueryParams();

  const intentAppDefId = intentApp?.appDefId;

  if (!intentAppDefId || ignoredApps.includes(intentAppDefId)) {
    return;
  }

  return { appDefId: intentAppDefId, version: intentApp.version };
};

const defaultIntentInstallReturn: IntentInstallReturnValue = {
  failedApps: [],
};

export class IntentInstallServices {
  constructor(
    private platformApiMethods: PlatformApiMethods,
    private documentServicesAPI: DocumentServicesObject,
    private experimentsAPI: IExperimentsApiAdapter,
  ) {}

  private intentInstallStore = new IntentInstallStore({
    options: {},
    appDefinitionIds: [],
  });

  private progressBarManager: ProgressBarManager | null = null;
  private panelsManager: PanelsManager | null = null;

  private applicationsInstaller = new ApplicationsInstaller();

  public async intentInstall(
    context: PlatformContext,
    originalOptions: IntentInstallOptions = {},
  ): Promise<IntentInstallReturnValue> {
    const host = getEditorInfo()?.host;

    const isImpersonated = getIsImpersonated();

    if (host === 'BLOCKS' || isImpersonated) {
      return defaultIntentInstallReturn;
    }

    if (this.intentInstallStore.isRunning()) {
      throw createIntentInstallError(
        IntentInstallErrorCode.IntentInstallIsAlreadyRunning,
      );
    }

    // for easier E2E testing. can return data only in debug mode
    const intentInstallTestingParams = getIntentInstallTestParams();

    const options: IntentInstallOptions = {
      ...originalOptions,
      isSilent: intentInstallTestingParams.isSilent || originalOptions.isSilent,
    };

    const appsToInstall = options.appsToInstall ?? [];
    const ignoredApps = options.ignoredApps ?? [];

    const intentApp = getIntentAppFromQueryParams(ignoredApps);

    const shouldInstallPending = options.shouldInstallPending ?? true;

    const canInstallAppPredicate = canInstallApp(options);

    const allPendingApps = shouldInstallPending
      ? getPendingApps(this.documentServicesAPI)
      : [];

    const pendingApps = allPendingApps.filter(canInstallAppPredicate);

    const intentAppFromPending = allPendingApps.find(
      (app) => app.appDefinitionId === intentApp?.appDefId,
    );

    const pendingAppDefs = pendingApps
      .map((app) => app.appDefinitionId)
      .concat(intentInstallTestingParams.pendingApps);

    const ignoredAppsSet = new Set(ignoredApps);

    const getAppsToInstallWithIntent = (apps: string[]): string[] => {
      if (!intentApp?.appDefId) {
        return apps;
      }

      if (
        intentAppFromPending &&
        !canInstallAppPredicate(intentAppFromPending)
      ) {
        return apps;
      }

      return [...apps, intentApp.appDefId];
    };

    const baseAppsToInstall = uniq(
      [...appsToInstall, ...pendingAppDefs].filter((appDefId) => {
        const isIntentApp = appDefId === intentApp?.appDefId;
        const isIgnoredApp = ignoredAppsSet.has(appDefId);

        return !isIntentApp && !isIgnoredApp;
      }),
    );

    const allAppsToInstall = getAppsToInstallWithIntent(baseAppsToInstall);

    const appDefIdsToInstall = allAppsToInstall.filter(
      (appDefId) => !this.documentServicesAPI.platform.isAppActive(appDefId),
    );

    this.intentInstallStore = new IntentInstallStore({
      options,
      appDefinitionIds: appDefIdsToInstall,
      intentApp,
    });
    this.progressBarManager = createProgressBarManager(
      this.platformApiMethods,
      this.intentInstallStore,
      options,
    );
    const biManager = new BIManager(this.intentInstallStore);

    biManager.reportIntentInstallBegin(IntentInstallBIEventStep.CHECK_INSTALL);

    if (!appDefIdsToInstall.length) {
      return defaultIntentInstallReturn;
    }

    this.intentInstallStore.setRunning(true);

    biManager.reportIntentInstallBegin(IntentInstallBIEventStep.BEGIN_INSTALL);

    // based on the options we can decide which installation strategy to use

    this.applicationsInstaller.setInstallationStrategy(
      new SerialInstallationStrategyV2(
        this.platformApiMethods,
        this.documentServicesAPI,
        this.experimentsAPI,
        this.intentInstallStore,
        this.progressBarManager,
      ),
    );

    this.panelsManager = new PanelsManager(
      this.platformApiMethods,
      this.intentInstallStore,
      this.progressBarManager,
    );

    const appDefIds = this.intentInstallStore.getAppsToInstall();

    this.progressBarManager.openInitialProgressBar();

    try {
      const appMarketListings =
        await appMarketService.getAppsInfoByLanguageWithDefaults({
          appDefinitionIds: appDefIds,
          languageCode: this.documentServicesAPI.generalInfo.getLanguage(),
        });
      this.intentInstallStore.setAppMarketListings(appMarketListings);

      if (
        this.experimentsAPI.enabled(PRE_INTENT_INSTALL_MODAL_EXPERIMENT) &&
        !options.isSilent
      ) {
        await this.panelsManager.openPreIntentInstallModal();
      }

      this.progressBarManager.startFakeProgressBarForCurrentApp();

      await this.applicationsInstaller.install(context, appDefIds, options);
    } catch (e: unknown) {
      throw createIntentInstallError(IntentInstallErrorCode.IntentInstallFailed)
        .withParentError(e as Error)
        .withApplicationsToInstall(appDefIdsToInstall)
        .withIntentApp(intentApp?.appDefId)
        .withIsSilent(options.isSilent);
    } finally {
      this.intentInstallStore.setRunning(false);

      this.progressBarManager.closeProgressBar();
      this.platformApiMethods.editor.updatePendingAppsNotification?.();
      this.panelsManager.openBusinessPanel();
      this.panelsManager.openPlatformFailedAlert();
    }

    await this.platformApiMethods.editor.saveInBackground?.();

    return { failedApps: this.intentInstallStore.getAppsWithErrors() };
  }

  public isIntentInstallRunning() {
    return this.intentInstallStore.isRunning();
  }

  public isIntentInstallRunningInSilentMode() {
    return this.isIntentInstallRunning() && this.intentInstallStore.isSilent();
  }

  public shouldUseWidgetAppsConfiguration() {
    if (!this.experimentsAPI.enabled(WIDGET_APPS_CONFIG_EXPERIMENT)) {
      return false;
    }

    return (
      this.intentInstallStore.isRunning() && !this.intentInstallStore.isSilent()
    );
  }

  public async setupIntentInstallWidgetsConfigFromUnifiedCollection(
    collection: UnifiedComponentsCollection,
    appName: string,
  ): Promise<void> {
    if (!this.shouldUseWidgetAppsConfiguration()) {
      return;
    }

    const widgets = Object.values(collection.widgets);
    const pages = Object.values(collection.pages);

    const filteredWidgets = widgets.filter(
      (widget) => widget.installation?.base?.essential,
    );

    this.intentInstallStore.setWidgetsConfiguration(
      (await this.panelsManager?.openWidgetAppsIntentInstallModal(
        filteredWidgets,
        pages,
        appName,
      )) ?? {},
    );
  }

  public getWidgetsConfiguration() {
    return this.intentInstallStore.getWidgetsConfiguration();
  }

  public getBIExtras(options?: GetBiExtrasOptions): {
    appType?: string;
    installationId?: string;
    origin?: string;
  } {
    if (!this.intentInstallStore.isRunning()) {
      return {};
    }

    const appType = (() => {
      if (!options?.appDefinitionId) {
        return;
      }

      return this.intentInstallStore.getIntentApp()?.appDefId ===
        options.appDefinitionId
        ? 'INTENT'
        : 'DEFAULT';
    })();

    return {
      installationId: this.intentInstallStore.getInstallationId(),
      origin: this.intentInstallStore.getOriginType(),
      ...(appType ? { appType } : {}),
    };
  }
}
